import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

// Mirrors backend/app/models/parts.py::InventoryForm. Lowercase wire
// format; the UI capitalizes for display (see formatInventoryForm).
export type InventoryForm =
  | 'reel'
  | 'cut_tape'
  | 'single'
  | 'tube'
  | 'tray'
  | 'bag'
  | 'other';

export const INVENTORY_FORMS: InventoryForm[] = [
  'reel',
  'cut_tape',
  'single',
  'tube',
  'tray',
  'bag',
  'other',
];

export function formatInventoryForm(form: InventoryForm | string | null | undefined): string {
  if (!form) return '—';
  switch (form) {
    case 'cut_tape':
      return 'Cut tape';
    case 'reel':
      return 'Reel';
    case 'single':
      return 'Single';
    case 'tube':
      return 'Tube';
    case 'tray':
      return 'Tray';
    case 'bag':
      return 'Bag';
    case 'other':
      return 'Other';
    default:
      return form;
  }
}

export interface InventoryWithLocation {
  part_id: number;
  quantity_on_hand: number;
  quantity_is_approximate: boolean;
  quantity_threshold: number;
  location_id: number | null;
  location_name: string | null;
  form: InventoryForm | null;
  last_counted_at: string | null;
}

export interface InventoryUpdateInput {
  quantity_on_hand?: number;
  quantity_is_approximate?: boolean;
  quantity_threshold?: number;
  location_id?: number | null;
  form?: InventoryForm | null;
  mark_counted?: boolean;
}

export function useUpdateInventory(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InventoryUpdateInput) =>
      api.patchJson<InventoryWithLocation>(
        `/api/orgs/${orgSlug}/parts/${partId}/inventory`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['part', orgSlug, partId] });
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
      qc.invalidateQueries({ queryKey: ['locations', orgSlug] });
      qc.invalidateQueries({ queryKey: ['location-parts', orgSlug] });
    },
  });
}
